/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
} from "@angular/core";
import { DomSanitizer, type SafeHtml } from "@angular/platform-browser";
import { map } from "rxjs/operators";

export const FILE_TYPES_ICONS = [
	"aep",
	"ai",
	"audio",
	"avi",
	"code",
	"css",
	"csv",
	"dmg",
	"doc",
	"docx",
	"eps",
	"empty",
	"exe",
	"fig",
	"folder",
	"gif",
	"html",
	"image",
	"indd",
	"java",
	"jpeg",
	"jpg",
	"js",
	"json",
	"mkv",
	"mp3",
	"mp4",
	"mpeg",
	"pdf",
	"pdf-simple",
	"png",
	"ppt",
	"pptx",
	"psd",
	"rar",
	"rss",
	"sql",
	"svg",
	"spreadsheet",
	"tiff",
	"txt",
	"video-01",
	"video-02",
	"wav",
	"webp",
	"xls",
	"xlsx",
	"xml",
	"zip",
] as const;

export type FILE_TYPES_ICONS = (typeof FILE_TYPES_ICONS)[number];

@Component({
	selector: "cb-filetype-icon",

	imports: [CommonModule],
	templateUrl: "./filetype-icon.component.html",
	styleUrl: "./filetype-icon.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiletypeIconComponent {
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	/**
	 * This is an input property that accepts a string as the file type.
	 */
	public readonly filetype = input<FILE_TYPES_ICONS | string>();

	public readonly url = input<string>();

	/**
	 * This is an input property that accepts a string as the type.
	 */
	public readonly type = input<"simple" | "gray" | "solid">("simple");

	/**
	 * This is an input property that accepts a string as the color.
	 */
	public readonly color = input<"brand" | "default">("default");

	private readonly computedFiletype = computed(() => {
		const url = this.url();
		const filetype = this.filetype()?.toLowerCase();

		if (url) {
			const lastDot = url.lastIndexOf(".");
			const value = url.slice(lastDot + 1).toLowerCase() as FILE_TYPES_ICONS;


			if (FILE_TYPES_ICONS.includes(value)) {
				return value as FILE_TYPES_ICONS;
			}
			return "empty";
		}

		if (filetype) {
			return filetype;
		}

		return "empty";
	});

	public svgContent = signal<SafeHtml | null>(null);

	private readonly httpClient: HttpClient = inject(HttpClient);
	private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

	constructor() {
		effect(() => {
			requestAnimationFrame(() => {
				const svgUrl = `/assets/file-icons/${this.type()}/${this.computedFiletype()}.svg`;
				this.httpClient
					.get(svgUrl, { responseType: "text" })
					.pipe(map((svg) => this.sanitizer.bypassSecurityTrustHtml(svg)))
					.subscribe((sanitizedSvg) => {
						this.svgContent.set(sanitizedSvg);
						this.cdr.detectChanges();
					});
			});
		});
	}
}
